'use client';

import Image from 'next/image';

import Skeleton from '@/app/(football)/[team]/_components/common/Skeleton';
import { cn } from '@/app/lib/utils/tailwindcss';

const DEFAULT_PROFILE_IMAGE = '/images/@/@default.svg';

export interface MiniGameRankingListItemProps {
  isMine?: boolean;
  isFirstPlace?: boolean;
  ranking: number;
  profileImage?: string | null;
  nickName: string;
  value: number;
}

export function MiniGameRankingListItem({
  isMine = false,
  isFirstPlace = false,
  ranking,
  profileImage,
  nickName,
  value,
}: MiniGameRankingListItemProps) {
  const isOverMax = ranking > 999;

  return (
    <li
      className={cn(
        'relative flex w-full items-center gap-2 rounded py-3 pr-4 pl-2',
        'shadow-[0px_0px_1.938px_0px_rgba(0,21,45,0.1)] gradient-border gradient-border-mini-game-ranking-list',
        isMine
          ? 'bg-mini-game-ranking-list-my-bg'
          : isFirstPlace
            ? 'bg-mini-game-ranking-list-first-bg'
            : 'bg-mini-game-ranking-list-default-bg',
      )}
    >
      {/* 랭킹 + 프로필 */}
      <div className="flex w-full items-center gap-1 self-stretch">
        {/* 랭킹 */}
        <div className={cn('flex h-9 w-9 shrink-0 items-center gap-0', isOverMax ? 'justify-start' : 'justify-center')}>
          {ranking === 0 && value === 0 && (
            <p className="text-center text-sm leading-[18px] font-bold text-mini-game-ranking-list-text">-</p>
          )}

          {ranking > 0 && ranking <= 999 && (
            <p className="text-center text-sm leading-[18px] font-bold text-mini-game-ranking-list-text">{ranking}</p>
          )}

          {isOverMax && (
            <>
              <p className="text-center text-sm leading-[18px] font-bold text-mini-game-ranking-list-text">999</p>
              <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                <g clipPath="url(#clip0_1149_738)">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.6 3.2V0H3.4V3.2H0V4.4H3.4V8H4.6V4.4H8V3.2H4.6Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_1149_738">
                    <rect width="8" height="8" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </>
          )}
        </div>

        {/* 프로필 */}
        <div className="flex w-full items-center gap-2">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#630209]">
            <Image src={profileImage ?? DEFAULT_PROFILE_IMAGE} alt={`${nickName} 프로필 이미지`} width={36} height={36} />
          </div>
          <p className="line-clamp-1 w-full text-sm leading-[18px] font-bold break-all text-mini-game-ranking-list-text">
            {nickName}
          </p>
        </div>
      </div>

      {/* 값 */}
      <p className="w-[35px] text-right text-sm leading-[18px] font-bold text-mini-game-ranking-list-text">{value}</p>
    </li>
  );
}

export function MiniGameRankingListItemSkeleton() {
  return (
    <li className="relative flex w-full items-center gap-2 rounded py-3 pr-4 pl-2 gradient-border gradient-border-mini-game-ranking-list bg-mini-game-ranking-list-default-bg">
      <div className="flex w-full items-center gap-1 self-stretch">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center">
          <Skeleton width={28} height={28} />
        </div>
        <div className="flex w-full items-center gap-2">
          <Skeleton width={36} height={36} borderRadius={99} className="shrink-0" />
          <Skeleton width="100%" height={18} />
        </div>
      </div>
      <Skeleton width={35} height={18} />
    </li>
  );
}

export interface MiniGameRankingListEmptyProps {
  imageSrc: string;
  imageAlt: string;
  children: React.ReactNode;
}

export function MiniGameRankingListEmpty({ imageSrc, imageAlt, children }: MiniGameRankingListEmptyProps) {
  return (
    <li className="relative flex w-full flex-col items-center justify-center gap-4 rounded bg-white/8 px-4 py-6 gradient-border gradient-border-mini-game-ranking-list-empty">
      <Image src={imageSrc} alt={imageAlt} width={48} height={48} />
      <p className="w-full text-center typo-body-6 text-g-00">{children}</p>
    </li>
  );
}
